import React from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';

export interface DetailPageHeaderProps {
  /** Texto del botón volver */
  backLabel: string;
  /** Callback al hacer clic en volver */
  onBack: () => void;
  /** Título principal (nombre del recurso) */
  title: string;
  /** Subtítulo (ej: producto • creado hace X) */
  subtitle: string;
  /** Icono o imagen opcional (ej: logo OS, tecnología). Se muestra en 12x12. */
  icon?: React.ReactNode;
  /** Acciones del header (botones a la derecha) */
  actions?: React.ReactNode;
  /** Banners de estado opcionales (creando, eliminando, etc.) que se muestran debajo del header */
  statusBanners?: React.ReactNode;
}

/**
 * Header unificado para páginas de detalle de productos (VM, Deploy App, Database, etc.).
 * Diseño basado en VMDetail: botón volver | divisor | icono + título/subtítulo | acciones.
 */
const DetailPageHeader: React.FC<DetailPageHeaderProps> = ({
  backLabel,
  onBack,
  title,
  subtitle,
  icon,
  actions,
  statusBanners,
}) => {
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
          >
            <ArrowLeftOutlined />
            <span className="text-sm font-medium hidden sm:inline">{backLabel}</span>
          </button>
          <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
          {icon != null && (
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {title}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {subtitle}
            </p>
          </div>
        </div>

        {actions != null && (
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 sm:flex-shrink-0">
            {actions}
          </div>
        )}
      </div>

      {statusBanners != null && <div className="space-y-0">{statusBanners}</div>}
    </>
  );
};

export default DetailPageHeader;
